<template>
  <Container padding-top full-width :class="$style['wrapper']">
    <Breadcrumb :items="breadcrumbItems" />

    <div :class="$style['content']">
      <ProductGallery :images="imageIds" :title="product?.title ?? ''" />

      <div ref="info" :class="$style['info']" :style="infoTop ? { '--info-top': infoTop } : undefined">
        <div :class="$style['info__head']">
          <h1 :class="$style['info__title']">{{ product?.title }}</h1>
          <p :class="$style['info__price']">Vanaf €6,-</p>
          <p v-if="product?.description" :class="$style['info__description']">{{ product.description }}</p>
        </div>

        <!-- One heading per page stands out: this one is for screen readers, the button says what it does -->
        <section :class="$style['reserve']" aria-labelledby="reserve-title">
          <h2 id="reserve-title" :class="$style['sr-only']">Reserveren</h2>
          <!-- Once it is in the reservation: one confirmation, with the next step as the main button -->
          <div v-if="inBag" :class="[$style['reserve__added'], justAdded && $style['reserve__added--new']]">
            <Icon name="CheckCircle" :class="$style['reserve__added-icon']" />
            <div>
              <p :class="$style['reserve__added-title']">{{ justAdded ? 'Toegevoegd aan je reservering' : 'Staat in je reservering' }}</p>
              <p v-if="stock > 1" :key="inBag" :class="$style['reserve__added-count']">{{ inBagLabel }}</p>
            </div>
            <!-- First the preview of everything in it, from there on to sending it -->
            <Button
              ref="nextButton"
              variant="primary"
              type="button"
              aria-haspopup="dialog"
              icon="ArrowRight"
              icon-pos="right"
              :class="$style['reserve__next']"
              @click="showBag">
              Naar je reservering
            </Button>
          </div>

          <!-- Always one line: where the full label doesn't fit next to the number, it says "Toevoegen" -->
          <div v-if="maxToAdd > 0" :class="[$style['reserve__row'], maxToAdd > 1 && $style['reserve__row--stepper']]">
            <FormStepper v-if="maxToAdd > 1" v-model="addQuantity" label="Aantal" hide-label :max="maxToAdd" />
            <Button :variant="inBag ? 'outline' : 'primary'" type="button" :class="$style['reserve__add']" @click="addToBag">
              <template v-if="inBag">Meer toevoegen</template>
              <template v-else>
                <span :class="$style['reserve__long']">Voeg toe aan reservering</span>
                <span :class="$style['reserve__short']">Toevoegen</span>
              </template>
            </Button>
          </div>

          <p :class="$style['sr-only']" role="status">{{ announcement }}</p>
        </section>

        <!-- What it is, as a short list; a colour, theme or category opens the items with it -->
        <dl :class="$style['details']">
          <div v-for="row in details" :key="row.label" :class="$style['details__row']">
            <dt>{{ row.label }}</dt>
            <dd v-if="row.text">{{ row.text }}</dd>
            <dd v-else>
              <template v-for="(link, i) in row.links" :key="link.title">
                <NuxtLink :to="link.to" :class="$style['details__link']">{{ link.title }}</NuxtLink><template v-if="i < (row.links?.length ?? 0) - 1">, </template>
              </template>
            </dd>
          </div>
        </dl>
      </div>
    </div>

    <section v-if="firstCategory && moreProducts.length" :class="$style['more']" aria-labelledby="more-title">
      <Rail :items="moreProducts" :label="`Meer ${firstCategory.title.toLowerCase()}`">
        <template #title>
          <h2 id="more-title" :class="$style['more__title']">Meer {{ firstCategory.title.toLowerCase() }}</h2>
        </template>
        <template #item="{ item }">
          <ProductCard :product="item" :to="{ name: 'kleding-product-slug-id', params: { id: item.id, slug: item.slug } }" />
        </template>
      </Rail>
      <Button variant="outline" :to="{ name: 'kleding-slug', params: { slug: firstCategory.slug } }">
        Alle {{ firstCategory.title.toLowerCase() }}
      </Button>
    </section>

    <!-- What goes with it for the same theme, from other categories too: the rest of the outfit -->
    <section v-if="themeName && themeProducts.length" :class="$style['more']" aria-labelledby="theme-title">
      <Rail :items="themeProducts" :label="`Meer ${themeName}`">
        <template #title>
          <h2 id="theme-title" :class="$style['more__title']">Meer {{ themeName }}</h2>
        </template>
        <template #item="{ item }">
          <ProductCard :product="item" :to="{ name: 'kleding-product-slug-id', params: { id: item.id, slug: item.slug } }" />
        </template>
      </Rail>
    </section>
  </Container>
</template>

<script setup lang="ts">
import { LABEL_THEMES, themeInSentence } from '~/composables/useHrMeta'

definePageMeta({
  layout: 'rental'
})
interface Category {
  id: number
  slug: string
  title: string
}

interface Location {
  id: number
  title: string
}

interface Color {
  id: number
  title: string
  slug: string
  hex?: string
}

interface Theme {
  id: number
  title: string
  slug: string
}

interface Product {
  id: number
  slug: string
  title: string
  quantity: number
  description?: string
  images?: (string | {fileId: string})[]
  categories?: Category[]
  locations?: Location[]
  colors?: Color[]
  themes?: Theme[]
}

const route = useRoute()
const { formatProduct } = useHrMeta()
const { addItem, itemCount, quantityOf } = useShoppingBag()
const { show: showBag } = useBagPreview()
const { data: product, error } = await useApi<Product>(`/products/${route.params.id}`)

const addQuantity = ref(1)
const justAdded = ref(false)
const announcement = ref('')
const nextButton = ref<{ $el: HTMLElement }>()
const stock = computed(() => product.value?.quantity ?? 1)
const inBag = computed(() => product.value ? quantityOf(product.value.id) : 0)
// You can't reserve more than the revue has
const maxToAdd = computed(() => Math.max(0, stock.value - inBag.value))
const inBagLabel = computed(() => inBag.value >= stock.value
  ? `Alle ${stock.value} exemplaren, meer hebben we er niet.`
  : `${inBag.value} ${inBag.value === 1 ? 'exemplaar' : 'exemplaren'}`)

async function addToBag () {
  if (!product.value) { return }
  const img = product.value.images?.[0]
  addItem({
    id: product.value.id,
    slug: product.value.slug,
    title: product.value.title,
    imageFileId: img ? (typeof img === 'string' ? img : img.fileId) : null,
    stock: product.value.quantity
  }, Math.min(addQuantity.value, maxToAdd.value))
  addQuantity.value = 1
  justAdded.value = true
  announcement.value = `Toegevoegd aan je reservering. Daar ${itemCount.value === 1 ? 'staat nu 1 item' : `staan nu ${itemCount.value} items`}.`
  // The add button is gone once every copy is in: keep the keyboard on the next step
  await nextTick()
  if (!maxToAdd.value) { nextButton.value?.$el?.focus() }
}

// Handle 404
if (error.value || !product.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Product niet gevonden'
  })
}

const firstCategory = computed(() => product.value?.categories?.[0])
const imageIds = computed(() => (product.value?.images ?? []).map(image => typeof image === 'string' ? image : image.fileId))

const breadcrumbItems = computed(() => [
  { label: 'Kleding verhuur', to: { name: 'kleding' } },
  ...(firstCategory.value ? [{ label: firstCategory.value.title, to: { name: 'kleding-slug', params: { slug: firstCategory.value.slug } } }] : []),
  { label: product.value?.title ?? '' }
])


// A colour or theme opens the category with that filter on
const filterLink = (type: 'colors' | 'themes', slug: string) => firstCategory.value
  ? { name: 'kleding-slug', params: { slug: firstCategory.value.slug }, query: { [type]: slug } }
  : { name: 'kleding' }

const details = computed<Array<{ label: string, text?: string, links?: Array<{ title: string, to: object }> }>>(() => [
  { label: 'In de collectie', text: stock.value > 1 ? `${stock.value} exemplaren` : 'Eén exemplaar' },
  {
    label: 'Kleuren',
    links: (product.value?.colors ?? []).map(color => ({ title: color.title.trim(), to: filterLink('colors', color.slug) }))
  },
  {
    label: "Thema's",
    links: (product.value?.themes ?? []).map(theme => ({ title: theme.title.trim(), to: filterLink('themes', theme.slug) }))
  },
  {
    label: (product.value?.categories?.length ?? 0) > 1 ? 'Categorieën' : 'Categorie',
    links: (product.value?.categories ?? []).map(category => ({ title: category.title.trim(), to: { name: 'kleding-slug', params: { slug: category.slug } } }))
  }
].filter(row => row.text || row.links?.length))

// The text next to the photos stays in view while you scroll past them. When it is taller than the
// screen, it sticks with its bottom in view instead, so nothing in it is out of reach.
const info = ref<HTMLElement>()
const infoTop = ref<string>()
let infoObserver: ResizeObserver | undefined
const placeInfo = () => {
  const element = info.value
  if (!element) { return }
  const header = document.querySelector('header')?.offsetHeight ?? 0
  const room = window.innerHeight - header - 24
  infoTop.value = element.offsetHeight > room ? `${(window.innerHeight - element.offsetHeight - 24) / 16}rem` : undefined
}
onMounted(() => {
  placeInfo()
  infoObserver = new ResizeObserver(placeInfo)
  if (info.value) { infoObserver.observe(info.value) }
  window.addEventListener('resize', placeInfo)
})
onBeforeUnmount(() => {
  infoObserver?.disconnect()
  window.removeEventListener('resize', placeInfo)
})

// More from the same category, loaded after the page itself
const { data: moreResponse } = useApi<{ data: Array<{ id: number, slug: string, title: string, images?: string[] }> }>('/products', {
  key: `more-${route.params.id}`,
  params: { categories: firstCategory.value?.slug, exclude: product.value?.id, limit: 12 },
  lazy: true,
  server: false
})
const moreProducts = computed(() => moreResponse.value?.data ?? [])

// The theme it is worn for (not a label such as "chique"), for what goes with it
const theme = computed(() => product.value?.themes?.find(item => !LABEL_THEMES.includes(item.title.trim().toLowerCase())))
const themeName = computed(() => theme.value ? themeInSentence(theme.value.title) : '')
const { data: themeResponse } = useApi<{ data: Array<{ id: number, slug: string, title: string, images?: string[] }> }>('/products', {
  key: `theme-${route.params.id}`,
  params: { themes: theme.value?.slug, exclude: product.value?.id, limit: 24 },
  lazy: true,
  server: false,
  immediate: !!theme.value
})
// Not what the row above already shows
const themeProducts = computed(() => {
  const shown = new Set(moreProducts.value.map(item => item.id))
  return (themeResponse.value?.data ?? []).filter(item => !shown.has(item.id)).slice(0, 12)
})
// Setup SEO
formatProduct({ product: product.value })
</script>

<style module>
.wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

/* The photos large, the text next to them at a width that reads well */
.content {
  display: grid;
  grid-template-columns: minmax(0, 40rem) minmax(21rem, 1fr);
  align-items: start;
  gap: var(--spacing-xxl) clamp(2rem, 1rem + 3vw, 4.5rem);
}

.info {
  --info-gap: var(--spacing-xxl);

  position: sticky;
  top: var(--info-top, calc(var(--stage-header) + var(--spacing-l)));
  display: flex;
  flex-direction: column;
  gap: var(--info-gap);
  max-width: 34rem;
}

/* What it is, then reserving it, then the details: more room between those than within them */
.info__head {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
}

/* The one heading on the page, but a product name, not a show: a size below the page titles */
.info__title {
  margin: 0;
  font-size: var(--stage-title-page);
}

.info__price {
  margin: calc(-1 * var(--spacing-s)) 0 0;
  font-size: var(--font-size-large);
}

.info__description {
  margin: 0;
  font-size: var(--stage-text);
}

.reserve {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
  container-type: inline-size;
}

/* The confirmation: what is in, and the way on */
.reserve__added {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: var(--spacing-l) var(--spacing-m);
  padding: var(--spacing-l);
  border: var(--stage-hairline) solid var(--primary-color);
}

/* Lights up once, so you see where your click went */
.reserve__added--new {
  animation: reserve-added 1.2s var(--stage-ease) both;
}

@keyframes reserve-added {
  from {
    background-color: oklch(79% 0.156 72deg / 22%);
  }
}

.reserve__added-icon {
  align-self: start;
  width: 1.75rem;
  height: 1.75rem;
  color: var(--primary-color);
}

.reserve__added p {
  margin: 0;
}

.reserve__added-title {
  font-weight: var(--font-weight-bold);
  line-height: 1.3;
}

.reserve__added-count {
  color: var(--text-subtle);
  font-size: var(--font-size-small);
}

.reserve__added .reserve__next {
  grid-column: 1 / -1;
  justify-self: start;
}

/* The number and the button side by side */
.reserve__row {
  display: flex;
  align-items: center;
  gap: var(--spacing-m);
}

.reserve__row .reserve__add {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 3rem;
}

.reserve__short {
  display: none;
}

/* A narrow column (a phone, a tablet): the short label, so it stays on one line next to the number */
@container (width < 28rem) {
  .reserve__row--stepper .reserve__long {
    display: none;
  }

  .reserve__row--stepper .reserve__short {
    display: inline;
  }

  .reserve__row .reserve__add {
    --padding-x: var(--spacing-l);
  }
}

.sr-only {
  position: absolute;
  width: var(--stage-hairline);
  height: var(--stage-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.details {
  margin: 0;
  border-top: var(--stage-hairline) solid var(--stage-line-soft);
}

.details__row {
  display: grid;
  grid-template-columns: 7rem minmax(0, 1fr);
  gap: var(--spacing-l);
  padding-block: var(--spacing-m);
  border-bottom: var(--stage-hairline) solid var(--stage-line-soft);
  font-size: var(--font-size-small);
}

.details__row dt {
  color: var(--text-subtle);
}

.details__row dd {
  margin: 0;
}

.details__link {
  color: var(--text-base);
  text-decoration: underline;
  text-decoration-color: var(--stage-line);
  text-underline-offset: 0.2em;
}

.details__link:hover {
  color: var(--primary-color);
  text-decoration-color: currentcolor;
}

.more {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-xl);
  margin-top: var(--stage-rhythm);
}

.more > :first-child {
  align-self: stretch;
}

/* Below the product, not a second title: bold, but in sentence case and a size below the page title */
.more__title {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-large);
  line-height: 1.2;
  letter-spacing: 0;
  text-transform: none;
}

@media (width <= 50rem) {
  .content {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--spacing-xl);
  }

  .info {
    --info-gap: var(--spacing-xl);

    position: static;
    max-width: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .reserve__added--new {
    animation: none;
  }
}
</style>
